import {
  useCreateCompanyMutation,
  useCreateEntityMutation,
  useCreateUserMutation,
} from "@codegen/index";
import { CreateUserMutationVariables, EntityTypes } from "@codegen/schema";
import { X } from "@phosphor-icons/react";
import { entityOptions } from "@utils/entities";
import { describeMutationError } from "@utils/error";
import { isValidEmail, normalizeEmail } from "@utils/parsers";
import { useCallback, useMemo, useState } from "react";
import ArchetypeSelectorMemo from "./archetypeSelector";
import { Alert } from "./Alert";
import { Button } from "./button";
import { Combobox } from "./combobox";
import { Input } from "./input";
import Modal from "./modal";
import Spinner from "./spinner";

const EntitiesCreatorModal = (props: {
  onClose: () => void;
  onSubmit: (entity: { id: string; type: EntityTypes; name: string }) => void;
  entityType?: EntityTypes;
}) => {
  const [selectedEntityType, setSelectedEntityType] = useState<EntityTypes>(
    props.entityType ?? EntityTypes.User
  );
  const [createStatus, setCreateStatus] = useState<
    "idle" | "creating" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [userArchetype, setUserArchetype] = useState<string>("");

  const entityTypeSelector = useMemo(
    () => (
      <Combobox
        placeholder="Select entity type"
        options={
          entityOptions as {
            label: string;
            value: string;
          }[]
        }
        defaultValue={selectedEntityType}
        onSelect={(x: string) => setSelectedEntityType(x as EntityTypes)}
        disabled={props.entityType != null}
      />
    ),

    [props.entityType, selectedEntityType]
  );

  const [createUserMutation] = useCreateUserMutation();
  const [createCompanyMutation] = useCreateCompanyMutation();
  const [createEntityMutation] = useCreateEntityMutation();

  const createEntity = useCallback(async () => {
    if (name.trim() === "") return;

    const normalizedEmail = normalizeEmail(email);

    if (selectedEntityType === EntityTypes.User && !isValidEmail(email)) {
      setErrorMessage(
        normalizedEmail === ""
          ? "Email is required."
          : "Enter a valid email address."
      );
      setCreateStatus("error");
      return;
    }

    setErrorMessage(null);
    setCreateStatus("creating");

    if (selectedEntityType === EntityTypes.User) {
      const { data, errors } = await createUserMutation({
        variables: {
          input: {
            clientMutationId: "",
            user: {
              fullName: name,
              email: normalizedEmail,
              // Send null for an unselected archetype. The column is plain text,
              // so "" persists literally and is not a recognised archetype.
              archetype: userArchetype === "" ? null : userArchetype,
              // Cast required because codegen runs with `avoidOptionals: true`,
              // which types every input field as mandatory-but-nullable.
            } as CreateUserMutationVariables["input"]["user"],
          },
        },
        refetchQueries: ["AllContacts"],
      });

      if (errors != null) {
        console.error(errors);
        setCreateStatus("error");
        return;
      }

      if (
        (data?.createUser?.user?.additionalEntitiesByUserId.nodes ?? [])
          .length > 0
      ) {
        props.onSubmit({
          id:
            data?.createUser?.user?.additionalEntitiesByUserId.nodes[0]?.id ??
            "",
          type: EntityTypes.User,
          name: name,
        });
        return;
      }

      setCreateStatus("idle");
      props.onClose();
      return;
    }

    if (selectedEntityType === EntityTypes.Company) {
      const { data, errors } = await createCompanyMutation({
        variables: {
          input: {
            company: {
              dbaName: name,
            },
          },
        },
        refetchQueries: ["AllCompanies"],
      });

      if (errors != null) {
        console.error(errors);
        setCreateStatus("error");
        return;
      }

      if (
        (
          data?.createCompany?.company?.additionalEntitiesByCompanyId.nodes ??
          []
        ).length > 0
      ) {
        props.onSubmit({
          id:
            data?.createCompany?.company?.additionalEntitiesByCompanyId.nodes[0]
              ?.id ?? "",
          type: EntityTypes.Company,
          name: name,
        });
        return;
      }

      setCreateStatus("idle");
      props.onClose();
      return;
    }

    const { data, errors } = await createEntityMutation({
      variables: {
        input: {
          additionalEntity: {
            name: name,
            type: selectedEntityType,
          },
        },
      },
    });

    if (errors != null) {
      console.error(errors);
      setCreateStatus("error");
      return;
    }

    setCreateStatus("idle");
    return props.onSubmit({
      id: data?.createAdditionalEntity?.additionalEntity?.id ?? "",
      type: selectedEntityType,
      name: name,
    });
  }, [
    createCompanyMutation,
    createEntityMutation,
    createUserMutation,
    email,
    name,
    props,
    selectedEntityType,
    userArchetype,
  ]);

  // Apollo's default errorPolicy rejects rather than returning `errors`, so a
  // failed mutation has to be caught here to reach the UI.
  const doCreateEntity = useCallback(async () => {
    try {
      await createEntity();
    } catch (e) {
      console.error(e);
      setErrorMessage(describeMutationError(e, "Error creating entity"));
      setCreateStatus("error");
    }
  }, [createEntity]);

  return (
    <Modal>
      <div className="w-[700px] flex flex-col justify-center">
        <div className="flex flex-col gap-y-4">
          <div className="flex flex-row items-center">
            <h1>New Entity</h1>
            <Button
              className="ml-auto"
              variant={"link"}
              size={"sm"}
              onClick={props.onClose}
            >
              <X size={16} />
            </Button>
          </div>
          <div className="flex flex-row gap-x-3 items-center">
            <h4>Type</h4>
            {entityTypeSelector}
          </div>

          <div className="flex flex-col gap-y-1">
            <h4>
              {selectedEntityType === EntityTypes.User
                ? "Full Name"
                : selectedEntityType === EntityTypes.Company
                ? "DBA Name"
                : "Name"}
            </h4>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                selectedEntityType === EntityTypes.User
                  ? "John Doe"
                  : selectedEntityType === EntityTypes.Company
                  ? "Acme Inc"
                  : "Name"
              }
            />
          </div>

          {selectedEntityType === EntityTypes.User && (
            <>
              <div className="flex flex-col gap-y-1">
                <h4>Email</h4>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                />
              </div>

              <div className="flex flex-col gap-y-1">
                <h4>Archetype</h4>
                <ArchetypeSelectorMemo
                  selectedType={userArchetype}
                  onSelect={setUserArchetype}
                  className="bg-white"
                />
              </div>
            </>
          )}

          {createStatus === "error" && (
            <Alert variant="warning">
              {errorMessage ?? "Error creating entity"}
            </Alert>
          )}

          {/* Spinner sits before the button so it does not shift the button's
              position while a request is in flight. */}
          <div className="w-full flex flex-row gap-x-2 items-center justify-end">
            {createStatus === "creating" && <Spinner />}
            <Button
              variant={"accent"}
              disabled={
                name.trim() === "" ||
                (selectedEntityType === EntityTypes.User && email.trim() === "")
              }
              onClick={doCreateEntity}
            >
              Create Entity
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EntitiesCreatorModal;
